const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api'

class RoleService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/roles`
  }

  // Get auth token from localStorage
  getAuthHeaders() {
    const token = localStorage.getItem('token')
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  }

  // Get all roles
  async getAllRoles() {
    try {
      const response = await fetch(`${this.baseURL}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching roles:', error)
      throw error
    }
  }

  // Get role by ID
  async getRoleById(id) {
    try {
      const response = await fetch(`${this.baseURL}/${id}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching role:', error)
      throw error
    }
  }

  // Create new role
  async createRole(roleData) {
    try {
      const response = await fetch(`${this.baseURL}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(roleData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error creating role:', error)
      throw error
    }
  }

  // Update role
  async updateRole(id, roleData) {
    try {
      const response = await fetch(`${this.baseURL}/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(roleData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error updating role:', error)
      throw error
    }
  }

  // Delete role
  async deleteRole(id) {
    try {
      const response = await fetch(`${this.baseURL}/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error deleting role:', error)
      throw error
    }
  }

  // Get role statistics
  async getRoleStats() {
    try {
      const response = await fetch(`${this.baseURL}/stats`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching role stats:', error)
      throw error
    }
  }

  // Transform role data for frontend display
  transformRoleForDisplay(role) {
    // Transform permissions from backend format to frontend format
    const transformPermissions = (permissions) => {
      if (!permissions || !Array.isArray(permissions)) {
        return []
      }
      
      const permissionStrings = []
      permissions.forEach(permission => {
        if (permission.module && permission.actions && Array.isArray(permission.actions)) {
          permission.actions.forEach(action => {
            permissionStrings.push(`${permission.module}_${action}`)
          })
        }
      })
      
      return permissionStrings
    }

    return {
      id: role._id || role.id,
      roleCode: role.roleCode,
      roleName: role.roleName,
      displayRoleName: role.displayRoleName,
      description: role.description,
      hierarchy: role.hierarchy,
      status: role.isActive ? 'Active' : 'Inactive',
      permissions: transformPermissions(role.permissions),
      users: role.users || 0,
      createdDate: role.createdAt ? new Date(role.createdAt).toISOString().split('T')[0] : '',
      lastModified: role.updatedAt ? new Date(role.updatedAt).toISOString().split('T')[0] : '',
      redirectTo: role.redirectTo,
      isSystemRole: role.isSystemRole || false
    }
  }

  // Transform role data for backend submission
  transformRoleForSubmission(roleData) {
    // Transform permissions from array of strings to proper object structure
    const transformPermissions = (permissions) => {
      if (!permissions || !Array.isArray(permissions)) {
        return []
      }
      
      // Group permissions by module
      const moduleGroups = {}
      permissions.forEach(permission => {
        // Extract module and action from permission string (e.g., "sales_view" -> module: "sales", action: "view")
        const parts = permission.split('_')
        if (parts.length >= 2) {
          const action = parts.pop() // Get the last part as action
          const module = parts.join('_') // Join remaining parts as module
          
          if (!moduleGroups[module]) {
            moduleGroups[module] = []
          }
          moduleGroups[module].push(action)
        }
      })
      
      // Convert to the expected format
      return Object.entries(moduleGroups).map(([module, actions]) => ({
        module: module,
        actions: actions
      }))
    }

    return {
      roleCode: roleData.roleCode,
      roleName: roleData.roleName,
      displayRoleName: roleData.displayRoleName || roleData.roleName,
      description: roleData.description || '',
      hierarchy: parseInt(roleData.hierarchy) || 0,
      isActive: roleData.status === 'Active',
      permissions: transformPermissions(roleData.permissions),
      redirectTo: roleData.redirectTo || './default/sales/home'
    }
  }
}

export default new RoleService()
